<template>
  <div>

    <!-- Groups Banner -->
    <BannerComponent>
      <button
          class="banner-toggle-btn"
          @click="toggleGroupsOpen"
          :aria-expanded="groupsOpen"
      >
        <font-awesome-icon
            :icon="['fas', groupsOpen ? 'chevron-down' : 'chevron-right']"
        />
      </button>
      <h6 class="banner-heading">Groups</h6>
    </BannerComponent>

    <!-- Groups Section -->
    <div v-if="groupsOpen">
      <!-- Add Group Button -->
      <div class="add-group-button">
        <button class="pageButton" @click="openAddGroupModal">
          <h6>Add Group</h6>
        </button>
      </div>

      <!-- Search Bar -->
      <div class="search-bar" style="margin-top: 0.75rem;">
        <div class="search-input-wrapper">
          <font-awesome-icon :icon="['fas','magnifying-glass']" class="search-icon"/>
          <input
              type="text"
              v-model="groupsSearchTerm"
              placeholder="Search Groups..."
              class="search-input cardBackground"
              @input="goToGroupsPage(1)"
          />
        </div>
        <button class="filter-btn" @click="goToGroupsPage(1)">
          <font-awesome-icon icon="filter"/>
        </button>
      </div>

      <!-- Groups List -->
      <div class="list" style="margin-top:1rem;">
        <div v-if="!(pagedGroups.length === 0)" class="list__item">
          <div class="group‐column group-name"><strong>Name</strong></div>
          <div class="group‐column group-id" style="margin-left: 2.5rem;"><strong>ID</strong></div>
          <div class="group‐column num-users" style="margin-left: 2rem; white-space: nowrap"><strong>Number <br>of Users</strong></div>
          <div style="padding-left: 5rem;"></div>
        </div>
        <div
            class="list__item"
            v-for="group in pagedGroups"
            :key="group.id"
        >
          <div class="group‐column group-name">{{ group.name }}</div>
          <div class="group‐column group-id">{{ group.id }}</div>
          <div class="group‐column num-users">{{ group.accounts.length }}</div>
<!--           TODO: Change this button name-->
          <div @click="onGroupInfo(group)" style="text-align:center;cursor:pointer;">
            <font-awesome-icon icon="info-circle" class="infoIcon"/>
          </div>
        </div>
        <div v-if="pagedGroups.length===0" class="no-results">No groups found</div>
      </div>

      <!-- Groups Pagination -->
      <div class="pagination-controls">
        <button
            class="page-arrow"
            :disabled="currentGroupsPage===1"
            @click="goToGroupsPage(currentGroupsPage-1)"
        >
          <font-awesome-icon icon="fa-angles-left"/>
        </button>
        <button
            v-for="p in visibleGroupsPages"
            :key="p"
            class="page-number"
            :class="{ 'active-page': p===currentGroupsPage }"
            @click="goToGroupsPage(p)"
        >{{ p }}</button>
        <button
            class="page-arrow"
            :disabled="currentGroupsPage===groupsPageCount"
            @click="goToGroupsPage(currentGroupsPage+1)"
        >
          <font-awesome-icon icon="fa-angles-right"/>
        </button>
      </div>
    </div>

    <!-- Accounts Banner -->
    <BannerComponent>
      <button
          class="banner-toggle-btn"
          @click="toggleAccountsOpen"
          :aria-expanded="accountsOpen"
      >
        <font-awesome-icon
            :icon="['fas', accountsOpen ? 'chevron-down' : 'chevron-right']"
        />
      </button>
      <h6 class="banner-heading">Accounts</h6>
    </BannerComponent>

    <!-- Accounts Section -->
    <div v-if="accountsOpen">

      <div class="add-group-button">
        <button class="pageButton" @click="openAddAccountModal">
          <h6>Add Account</h6>
        </button>
      </div>

      <!-- Search Bar -->
      <div class="search-bar" style="margin-top: 0.75rem;">
        <div class="search-input-wrapper">
          <font-awesome-icon :icon="['fas','magnifying-glass']" class="search-icon"/>
          <input
              type="text"
              v-model="accountsSearchTerm"
              placeholder="Search Accounts..."
              class="search-input cardBackground"
              @input="goToAccountsPage(1)"
          />
        </div>
        <button class="filter-btn" @click="goToAccountsPage(1)">
          <font-awesome-icon icon="filter"/>
        </button>
      </div>

      <!-- Accounts List -->
      <div class="list" style="margin-top:1rem;">
        <div class="list__item header-row">
          <div class="acct-column acct-username"><strong>User</strong></div>
          <div class="acct-column acct-id"><strong>ID</strong></div>
          <div class="acct-column acct-last-action" style="flex: 2.5;"><strong>Last Action</strong></div>
        </div>
        <!-- data rows -->
        <div
              class="list__item"
              v-for="acct in pagedAccounts"
              :key="acct.id"
          >
          <div class="acct-column acct-username">{{ acct.username }}</div>
          <div class="acct-column acct-id">{{ acct.id }}</div>
          <div class="acct-column acct-last-action">{{ acct.last_action }}</div>
          <div class="acct-column acct-more-info" @click="onAccountInfo(acct)" style="cursor:pointer;">
            <font-awesome-icon icon="info-circle" class="infoIcon"/>
          </div>
        </div>
        <div v-if="pagedAccounts.length===0" class="no-results">No accounts found</div>
      </div>

      <!-- Accounts Pagination -->
      <div class="pagination-controls">
        <button
            class="page-arrow"
            :disabled="currentAccountsPage===1"
            @click="goToAccountsPage(currentAccountsPage-1)"
        >
          <font-awesome-icon icon="fa-angles-left"/>
        </button>
        <button
            v-for="p in visibleAccountsPages"
            :key="p"
            class="page-number"
            :class="{ 'active-page': p===currentAccountsPage }"
            @click="goToAccountsPage(p)"
        >{{ p }}</button>
        <button
            class="page-arrow"
            :disabled="currentAccountsPage===accountsPageCount"
            @click="goToAccountsPage(currentAccountsPage+1)"
        >
          <font-awesome-icon icon="fa-angles-right"/>
        </button>
      </div>
    </div>

    <!-- Add Group Modal -->
    <BaseModal
        :visible="visibleAddGroupModal"
        title="New Group"
        @close="closeAddGroupModal()"
        @submit="addGroup()"
        submitLabel="Add"
        :cancelShow=false
    >

      <!-- Adding Group -->
      <div class="text-box-label">
        Group Name:
      </div>
      <div class="text-box">
        <input
            type="text"
            class="fieldInput text-box-entry"
            v-model="newGroupName"
            placeholder="New Group Name"
        />
      </div>

      <div class="text-box-label" style="margin-top: 1rem;">
        Accounts:
      </div>
      <CustomSelect
          v-model="selectedAccounts"
          :options="accountsToSelect"
          :multiSelect="true"
          :show-select-all="true"
          placeholder="Select accounts..."
      />

    </BaseModal>

    <!-- Add Account Modal -->
    <BaseModal
        :visible="visibleAddAccountModal"
        title="New Account"
        @close="closeAddAccountModal()"
        @submit="addAccount()"
        submitLabel="Add"
        :cancelShow=false
    >

      <div class="text-box-label">
        Account Username:
      </div>
      <div class="text-box">
        <input
            type="text"
            class="fieldInput text-box-entry"
            v-model="newAccountUsername"
            placeholder="New Username"
        />
      </div>

      <div class="text-box-label">
        Password:
      </div>
      <div class="text-box">
        <input
            type="password"
            class="fieldInput text-box-entry"
            v-model="newPassword"
            placeholder="password"
        />
      </div>

      <div class="text-box-label">
        Confirm Password:
      </div>
      <div class="text-box">
        <input
            type="password"
            class="fieldInput text-box-entry"
            v-model="confirmPassword"
            placeholder="password"
        />
      </div>

      <div class="text-box-label">
        Select Groups:
      </div>
      <CustomSelect
          v-model="selectedGroups"
          :options="groupsToSelect"
          :multiSelect="true"
          :show-select-all="true"
          placeholder="Select groups..."
      />

    </BaseModal>

    <!-- Group Info Modal -->
    <BaseModal
        :visible="visibleGroupInfoModal"
        :title="`Group ${group.name}`"
        @close="closeGroupInfoModal()"
        @submit="groupInfoSubmit()"
        :submitLabel="groupInfoEditMode? 'Save' : 'Edit'"
        :cancelShow=false
    >
      <div v-if="groupInfoEditMode">
        <div class="text-box-label">
          Group Name:
        </div>
        <div class="text-box">
          <input
              type="text"
              class="fieldInput text-box-entry"
              v-model="newGroupName"
              :placeholder="this.group.name"
          />
        </div>

        <CustomSelect
            v-model="group.accounts"
            :options="accountsToSelect"
            :multiSelect="true"
            :show-select-all="true"
            placeholder="Select accounts..."
        />

      </div>

      <div v-else>
        <div class="text-box-label">
          Group Name:
        </div>
        <div class="text-box" style="margin-bottom: 0;">
          <div class="text-box-entry">
            {{this.group.name}}
          </div>
        </div>
      </div>

    </BaseModal>

    <!-- Account Info Modal -->
    <BaseModal
        :visible="visibleAccountInfoModal"
        :title="`Group ${account.username}`"
        @close="closeAccountInfoModal()"
        @submit="accountInfoSubmit()"
        :submitLabel="accountInfoEditMode? 'Save' : 'Edit'"
        :cancelShow=false
    >
      <div v-if="accountInfoEditMode">
        <div class="text-box-label">
          Account Username:
        </div>
        <div class="text-box">
          <input
              type="text"
              class="fieldInput text-box-entry"
              v-model="newAccountUsername"
              :placeholder="this.account.username"
          />
        </div>

        <div class="text-box-label">
          Password:
        </div>
        <div class="text-box">
          <input
              type="password"
              class="fieldInput text-box-entry"
              v-model="newPassword"
              placeholder="*********"
          />
        </div>

        <div class="text-box-label">
          Confirm Password:
        </div>
        <div class="text-box">
          <input
              type="password"
              class="fieldInput text-box-entry"
              v-model="confirmPassword"
              placeholder="*********"
          />
        </div>

        <div class="text-box-label">
          Select Groups:
        </div>
        <CustomSelect
            v-model="account.groups"
            :options="groupsToSelect"
            :multiSelect="true"
            :show-select-all="true"
            placeholder="Select groups..."
        />

      </div>

      <div v-else>
        <div class="text-box-label">
          Account Username:
        </div>
        <div class="text-box" style="margin-bottom: 0;">
          <div class="text-box-entry">
            {{this.account.username}}
          </div>
        </div>
      </div>

    </BaseModal>

    <MessageModal :message="appMessage.text" :type="appMessage.type" ref="messageModal" />

  </div>
</template>

<script>
import MessageModal from "@/components/MessageModal.vue";
import BannerComponent from "@/components/BannerComponent.vue";
import BaseModal from "@/components/BaseModal.vue";
import CustomSelect from "@/components/CustomSelect.vue";

export default {
  name: 'AccountsPage',
  components: {CustomSelect, BaseModal, BannerComponent, MessageModal},
  data(){
    return {
      appMessage: {
        text: '',
        type: 'error',
      },
      groupsOpen: false,
      accountsOpen: false,
      // TODO: Delete fake groups/accounts
      groups: [
        { name: 'admins',           id: 100, accounts: [2000,2001,2002,2003] },
        { name: 'developers',       id: 101, accounts: [2002,2004,2005]       },
        { name: 'operations',       id: 102, accounts: [2001,2006,2007]       },
        { name: 'support',          id: 103, accounts: [2003,2008,2009]       },
        { name: 'hr',               id: 104, accounts: [2010,2011]           },
        { name: 'finance',          id: 105, accounts: [2012,2000]           },
        { name: 'marketing',        id: 106, accounts: [2004,2013]           },
        { name: 'sales',            id: 107, accounts: [2005,2006,2014]       },
        { name: 'qa',               id: 108, accounts: [2007,2008]           },
        { name: 'design',           id: 109, accounts: [2009,2010]           },
        { name: 'security',         id: 110, accounts: [2011,2012,2001]       },
        { name: 'research',         id: 111, accounts: [2013,2002]           },
        { name: 'analytics',        id: 112, accounts: [2014,2003,2004]       },
        { name: 'customer-success', id: 113, accounts: [2005,2006]           },
        { name: 'it-support',       id: 114, accounts: [2007,2008,2009]       },
      ],
      accounts: [
        { username: 'alice',    fullname: 'Alice Johnson',    id: 2000, last_action: 'Logged in',               groups: [100,105]  },
        { username: 'bob',      fullname: 'Bob Smith',        id: 2001, last_action: 'Jun 9, 2025  3:11 PM',    groups: [100,110]  },
        { username: 'carol',    fullname: 'Carol Martinez',   id: 2002, last_action: 'Jun 10, 2025 8:45 AM',    groups: [101,111]  },
        { username: 'dave',     fullname: 'Dave Wilson',      id: 2003, last_action: 'Jun 11, 2025 12:00 PM',   groups: [100,103]  },
        { username: 'eve',      fullname: 'Eve Thompson',     id: 2004, last_action: 'Jun 12, 2025  9:30 AM',    groups: [101,112]  },
        { username: 'frank',    fullname: 'Frank Lee',        id: 2005, last_action: 'Jun 12, 2025 10:15 AM',    groups: [101,107]  },
        { username: 'grace',    fullname: 'Grace Kim',        id: 2006, last_action: 'Jun 12, 2025 11:00 AM',    groups: [102,107]  },
        { username: 'heidi',    fullname: 'Heidi Patel',      id: 2007, last_action: 'Jun 12, 2025 11:45 AM',    groups: [102,108,114] },
        { username: 'ivan',     fullname: 'Ivan Garcia',      id: 2008, last_action: 'Jun 12, 2025 12:30 PM',    groups: [103,108,114] },
        { username: 'judy',     fullname: 'Judy Nguyen',      id: 2009, last_action: 'Jun 12, 2025  1:15 PM',    groups: [103,109,114] },
        { username: 'mallory',  fullname: 'Mallory Young',    id: 2010, last_action: 'Jun 12, 2025  2:00 PM',    groups: [103,104,109] },
        { username: 'oscar',    fullname: 'Oscar Hernandez',  id: 2011, last_action: 'Jun 12, 2025  2:45 PM',    groups: [104,110]  },
        { username: 'peggy',    fullname: 'Peggy Walker',     id: 2012, last_action: 'Jun 12, 2025  3:30 PM',    groups: [104,105,110] },
        { username: 'trent',    fullname: 'Trent Robinson',   id: 2013, last_action: 'Jun 12, 2025  4:15 PM',    groups: [106,111,112] },
        { username: 'victor',   fullname: 'Victor Clark',     id: 2014, last_action: 'Jun 12, 2025  5:00 PM',    groups: [107,112]  },
      ],
      groupsSearchTerm: '',
      currentGroupsPage: 1,
      itemsPerGroupsPage: 5,
      accountsSearchTerm: '',
      currentAccountsPage: 1,
      itemsPerAccountsPage: 5,
      visibleAddGroupModal: false,
      visibleAddAccountModal: false,
      visibleGroupInfoModal: false,
      visibleAccountInfoModal: false,
      groupInfoEditMode: false,
      accountInfoEditMode: false,
      group: {
        name: '',
        id: null,
        accounts: [],
      },
      account: {
        username: '',
        id: null,
        groups: [],
      },
      newGroupName: '',
      selectedAccounts: [],
      newAccountUsername: '',
      newPassword: '',
      confirmPassword: '',
      selectedGroups: [],
    }
  },
  mounted(){

  },
  computed: {
    // --- GROUPS ---
    filteredGroups() {
      const t = this.groupsSearchTerm.trim().toLowerCase();
      if (!t) return this.groups;
      return this.groups.filter(g =>
          g.name.toLowerCase().includes(t) ||
          String(g.id).includes(t)
      );
    },
    groupsPageCount() {
      return Math.ceil(this.filteredGroups.length / this.itemsPerGroupsPage);
    },
    pagedGroups() {
      const start = (this.currentGroupsPage - 1) * this.itemsPerGroupsPage;
      return this.filteredGroups.slice(start, start + this.itemsPerGroupsPage);
    },
    visibleGroupsPages() {
      const low = Math.max(1, this.currentGroupsPage - 1);
      const high = Math.min(this.groupsPageCount, this.currentGroupsPage + 1);
      return Array.from({ length: high - low + 1 }, (_, i) => low + i);
    },

    // --- ACCOUNTS ---
    filteredAccounts() {
      const t = this.accountsSearchTerm.trim().toLowerCase();
      if (!t) return this.accounts;
      return this.accounts.filter(a =>
          a.username.toLowerCase().includes(t) ||
          a.fullname .toLowerCase().includes(t) ||
          String(a.id).includes(t)
      );
    },
    accountsPageCount() {
      return Math.ceil(this.filteredAccounts.length / this.itemsPerAccountsPage);
    },
    pagedAccounts() {
      const start = (this.currentAccountsPage - 1) * this.itemsPerAccountsPage;
      return this.filteredAccounts.slice(start, start + this.itemsPerAccountsPage);
    },
    visibleAccountsPages() {
      const low = Math.max(1, this.currentAccountsPage - 1);
      const high = Math.min(this.accountsPageCount, this.currentAccountsPage + 1);
      return Array.from({ length: high - low + 1 }, (_, i) => low + i);
    },
    // --- OTHER FUNCTIONS ---
    accountsToSelect() {
      return this.accounts.map(a => ({
        id: a.id,
        name: a.username
      }));
    },
    groupsToSelect() {
      return this.groups.map(a => ({
        id: a.id,
        name: a.name
      }));
    }
  },
  methods: {
    openModal(modalName) {
      this[modalName] = true
    },
    closeModal(modalName) {
      this[modalName] = false
    },
    toggleGroupsOpen() {
      this.groupsOpen = !this.groupsOpen;
    },
    toggleAccountsOpen() {
      this.accountsOpen = !this.accountsOpen;
    },
    goToGroupsPage(p) {
      this.currentGroupsPage = Math.min(Math.max(p, 1), this.groupsPageCount);
    },
    goToAccountsPage(p) {
      this.currentAccountsPage = Math.min(Math.max(p, 1), this.accountsPageCount);
    },
    openAddGroupModal() {
      this.openModal('visibleAddGroupModal');
    },
    closeAddGroupModal() {
      this.newGroupName = '';
      this.selectedAccounts = [];
      this.closeModal('visibleAddGroupModal');
    },
    addGroup() {
      // TODO: This is a stub function for an API call to add a group. The id needs to be pulled from the database and not a hard-coded value
    if (this.selectedAccounts.length === 0) {
      this.appMessage.text = 'No Accounts Selected';
      this.appMessage.type = 'error';
      this.$refs.messageModal.open();
    }
    else if (this.newGroupName === "") {
      this.appMessage.text = 'Missing Group Name';
      this.appMessage.type = 'error';
      this.$refs.messageModal.open();
    }
    else {
        this.groups.push({name: this.newGroupName, id: 100, accounts: this.selectedAccounts})
        console.log("Created a group with account IDs: ", this.selectedAccounts)
        this.closeAddGroupModal();
      }
    },
    openAddAccountModal() {
      this.openModal('visibleAddAccountModal');
    },
    closeAddAccountModal() {
      this.newAccountUsername = '';
      this.newPassword = '';
      this.confirmPassword = '';
      this.selectedGroups = [];
      this.closeModal('visibleAddAccountModal');
    },
    addAccount() {
      // TODO: this is a stub for an API call to add an account
      if (this.newAccountUsername === "") {
        this.appMessage.text = 'No Username Entered.';
        this.appMessage.type = 'error';
        this.$refs.messageModal.open();
      }
      else if (this.newPassword === "") {
        this.appMessage.text = 'No Password Entered';
        this.appMessage.type = 'error';
        this.$refs.messageModal.open();
      }
      else if (this.newPassword !== this.confirmPassword) {
        this.appMessage.text = 'Passwords Do Not Match';
        this.appMessage.type = 'error';
        this.$refs.messageModal.open();
      }
      else{
        console.log("Created username:", this.newAccountUsername, "with password:", this.newPassword, "added to groups:", this.selectedGroups);
        this.closeAddAccountModal();
      }
    },
    onGroupInfo(group) {
      this.group.name = group.name;
      this.group.id = group.id;
      this.group.accounts = group.accounts;
      this.openModal('visibleGroupInfoModal');
    },
    groupInfoSubmit() {
      if (!this.groupInfoEditMode) {
        this.groupInfoEditMode = true;
      }
      else {
        if (this.group.accounts.length === 0) {
          this.appMessage.text = 'No Accounts Selected';
          this.appMessage.type = 'error';
          this.$refs.messageModal.open();
        }
        else {
          if (this.newGroupName === "") {
            this.newGroupName = this.group.name
          }
          // TODO: this is a stub for an API call to make a group
          console.log("Changed group", this.group.name, "to", this.newGroupName, "with members:", this.group.accounts)
          this.closeGroupInfoModal()
        }
      }
    },
    closeGroupInfoModal() {
      this.group.name = "";
      this.group.id = null;
      this.group.accounts = [];
      this.groupInfoEditMode = false;
      this.newGroupName = "";
      this.closeModal('visibleGroupInfoModal')
    },
    onAccountInfo(account) {
      this.account.username = account.username
      this.account.id = account.id
      this.account.groups = account.groups
      this.openModal('visibleAccountInfoModal')
    },
    accountInfoSubmit() {
      if (!this.accountInfoEditMode) {
        this.accountInfoEditMode = true;
      }
      else {
        if (this.account.groups.length === 0) {
          this.appMessage.text = 'No Groups Selected';
          this.appMessage.type = 'error';
          this.$refs.messageModal.open();
        }
        else if (this.newPassword !== this.confirmPassword) {
          this.appMessage.text = "Passwords don't match";
          this.appMessage.type = 'error';
          this.$refs.messageModal.open();
        }
        else {
          if (this.newPassword === "") {
            // TODO: Don't let them change the password if this is empty or come up with a better system of monitoring.
          }
          if (this.newAccountUsername === "") {
            this.newAccountUsername = this.account.username
          }
          // TODO: This is a stub do an API call for editing an account
          console.log("Changed account", this.account.username, "to", this.newAccountUsername, "with groups:", this.account.groups, "and password:", this.newPassword)
          this.closeAccountInfoModal();
        }
      }
    },
    closeAccountInfoModal() {
      this.visibleAccountInfoModal = false;
      this.accountInfoEditMode     = false;
      this.account = { username:'', id:null, groups:[] };
      this.newAccountUsername  = '';
      this.newPassword     = '';
      this.confirmPassword = '';
      this.selectedGroups  = [];
    },
  },
}
</script>

<style scoped>

.banner-heading {
  display: inline-block;
  margin-left: 0.5rem;
  font-size: 1.125rem;
  color: var(--text-primary);
}

.add-group-button {
  display: flex;
  justify-content: left;
  margin-left: 1rem;
  margin-top: 1rem;
  margin-bottom: 1.5rem;
}

.group‐column {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  color: var(--text-primary);
}

.group-name {
  flex: 0 0 5rem;       /* fixed 2rem for icon */
  justify-content: flex-start;
  overflow: hidden;
}

.group-id {
  flex: 0 0 3rem;
  justify-content: flex-start;
  align-items: center;
  margin-left: .25rem;
  overflow: hidden;
  margin-right: 0;
}

.num-users {
  flex: 0 0 2rem;
}

.acct-column {
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex: 1;
  color: var(--text-primary);
}

.acct-username    { flex: 1.5; justify-content: flex-start; }
.acct-id          { flex: 1; justify-content: flex-start; padding-left: .5rem; }
.acct-last-action { flex: 2; justify-content: flex-start; align-items: flex-start; padding-left: 1rem; }
.acct-more-info   { flex: 0 0 2rem; justify-content: flex-end; }



</style>
